/* eslint-disable react/no-unstable-nested-components */
import { Card, Stack } from "@mui/material";
import { DataGrid, gridStringOrNumberComparator } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import CircularProgress from "@mui/material/CircularProgress";
import { getAPICall, putAPICall } from "lib/callApi";
import { CRONS, PAUSE_CRON, UNPAUSE_CRON } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import "styles/snapshot.scss";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";
import { displayDate } from "utils/Date";

function Crons() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cronsData, setCronsData] = useState([]);

  const getCronsData = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(CRONS, true);
      if (result?.data?.success) {
        setCronsData(result?.data?.data || []);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not retrieve crons data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve crons data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeCronStatus = (data) => {
    Swal.fire({
      title: "Are you sure?",
      ...(data.status === "active"
        ? {
            text: `Are you sure you want to pause this cron? If so, please specify the duration for which you'd like to pause it, in hours.`,
            icon: "warning",
            input: "select",
            inputOptions: ["minute", "hour"].includes(data.repeatInterval)
              ? Object.fromEntries([...Array(24).keys()].map((key) => [key + 1, key + 1]))
              : { 24: 24 },
            inputPlaceholder: `Hours`,
            inputValidator: (inputValue) => !inputValue && "Hours is required",
            confirmButtonText: "Pause",
          }
        : {
            text: `Are you sure you want to Unpause this cron?`,
            icon: "warning",
            confirmButtonText: "Unpause",
          }),
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      reverseButtons: true,
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          const response = await putAPICall(
            data.status === "active" ? PAUSE_CRON : UNPAUSE_CRON,
            true,
            {
              id: data.id,
              ...(data.status === "active" ? { numberOfHours: result.value } : {}),
            }
          );
          if (response?.data?.success) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "success",
                title: "Success",
                text: `Cron ${data.status === "active" ? "paused" : "unpaused"} successfully!`,
              }),
              {
                variant: "custom",
              }
            );
            getCronsData();
          } else {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "error",
                text: `Could not ${
                  data.status === "active" ? "pause" : "unpause"
                } cron, Please try again!`,
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  function isNullOrEmpty(str) {
    return str === null || str === "";
  }

  // Custom sorter than puts null/empty values at the bottom of the sort
  const nullHandlingSortComparator = (v1, v2, cellParams1, cellParams2) => {
    if (isNullOrEmpty(v1) || isNullOrEmpty(v2)) {
      if (v1 === v2) return 0;
      const sortModel = cellParams1.api.getSortModel();
      const sortColumn = sortModel.find((sm) => sm.field === cellParams1.field);
      if (sortColumn && sortColumn.sort === "desc") {
        return isNullOrEmpty(v1) ? -1 : 1;
      }
      return isNullOrEmpty(v1) ? 1 : -1;
    }
    return gridStringOrNumberComparator(v1, v2, cellParams1, cellParams2);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      headerClassName: "datatable-header",
      width: 50,
    },
    {
      field: "name",
      headerName: "Cron Name",
      headerClassName: "datatable-header",
      minWidth: 250,
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      disableSortBy: true,
      renderCell: (params) => (
        <MDBox sx={{ display: "flex" }}>
          <MDBox>
            <MDBadge
              variant="contained"
              color={params.value === "active" ? "success" : "error"}
              badgeContent={params.value}
              container
            />
          </MDBox>
        </MDBox>
      ),
    },
    {
      field: "numberOfHours",
      headerName: "Hours",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "pausedUntil",
      headerName: "Paused Until",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      sortComparator: nullHandlingSortComparator,
      valueGetter: (params) =>
        params.value ? displayDate(params.value, "MM-DD-YYYY hh:mm a") : "",
    },
    {
      field: "repeatInterval",
      headerName: "",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
      disableSortBy: true,
      renderCell: (params) => (
        <MDBox sx={{ display: "flex" }}>
          <MDBox>
            <MDButton
              color={params.row.status === "active" ? "error" : "secondaryGreen"}
              size="small"
              onClick={() => handleChangeCronStatus(params.row)}
              disabled={isLoading}
            >
              {params.row.status === "active" ? "Pause" : "Unpause"}
            </MDButton>
          </MDBox>
        </MDBox>
      ),
    },
  ];

  useEffect(() => {
    const userData = localStorage.getItem("user");

    if (!userData) {
      navigate("/sign-in");
    }

    getCronsData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div role="tabpanel" id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`}>
        <MDBox sx={{ pt: 3 }}>
          <Card>
            <MDBox p={3} pb={0} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Alysio Crons
              </MDTypography>
            </MDBox>

            <MDBox
              sx={{
                width: "100%",
                overflowX: "auto",
                fontSize: "0.875rem",
              }}
            >
              {isLoading ? (
                <Stack alignItems="center">
                  <CircularProgress
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                    }}
                    color="info"
                    size="10vh"
                  />
                </Stack>
              ) : (
                ""
              )}

              <DataGrid
                sx={{
                  mt: "1rem",
                  "&.MuiTypography-root": {
                    fontSize: "1rem !important",
                  },
                }}
                rows={cronsData}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </MDBox>
          </Card>
        </MDBox>
      </div>
    </DashboardLayout>
  );
}

export default Crons;
